import { parseDocument } from "htmlparser2";
import { createStaticVNode, defineComponent, h, onErrorCaptured, ref, watch, type Component, type PropType, type VNode } from "vue";

type HtmlNode = ReturnType<typeof parseDocument>["children"][number];
type SectionNode =
  | { type: "static"; html: string }
  | { type: "element"; tag: string; attrs: Record<string, string>; children: SectionNode[] }
  | { type: "component"; tag: string; props: Record<string, unknown>; mountId: string };

const SectionBlock = defineComponent({
  props: {
    component: { type: [Object, Function] as PropType<Component>, required: true },
    componentProps: { type: Object as PropType<Record<string, unknown>>, required: true },
  },
  setup(props) {
    const failed = ref(false);
    watch(() => props.componentProps, () => { failed.value = false; });
    onErrorCaptured(error => {
      failed.value = true;
      console.error("Section rendering failed:", error);
      return false;
    });
    return () => failed.value ? null : h(props.component, props.componentProps);
  },
});

function componentProps(attrs: Record<string, string>): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(attrs)) {
    if (!name.startsWith("data-")) continue;
    const key = name.slice(5).replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
    const trimmed = value.trim();
    if (key === "config") {
      try { props[key] = JSON.parse(trimmed); }
      catch { props[key] = undefined; }
    } else {
      props[key] = trimmed === "true" ? true : trimmed === "false" ? false
        : /^-?\d+(\.\d+)?$/.test(trimmed) ? Number(trimmed) : trimmed;
    }
  }
  if (attrs.id) props.id = attrs.id;
  if (attrs.class) props.class = attrs.class;
  return props;
}

function mountId(tag: string, attrs: Record<string, string>, index: number): string {
  if (attrs.id) return `${tag}__${attrs.id}`;
  const signature = Object.entries(attrs).filter(([name]) => name !== "style")
    .map(([name, value]) => `${name}=${value}`).sort().join("|");
  let hash = 0;
  for (let i = 0; i < signature.length; i++) hash = (hash * 31 + signature.charCodeAt(i)) | 0;
  return `${tag}__${Math.abs(hash)}__${index}`;
}

export function parseSectionContent(html: string, componentTags: string[]): SectionNode[] {
  const tags = new Set(componentTags);
  const document = parseDocument(html, {
    withStartIndices: true, withEndIndices: true,
    lowerCaseAttributeNames: false, lowerCaseTags: false,
  });
  let componentIndex = 0;
  const convert = (node: HtmlNode): SectionNode => {
    if ("name" in node && "attribs" in node) {
      const tag = node.name.toLowerCase();
      // Benefit-strip labels keep their saved h4 styling without creating skipped page headings.
      if (tag === "h4" && node.attribs.class?.split(/\s+/).includes("grid2__title") && !node.attribs.role) {
        return { type: "element", tag: node.name, attrs: { ...node.attribs, role: "presentation" }, children: node.children.map(convert) };
      }
      if (tags.has(tag)) {
        const attrs = Object.fromEntries(Object.entries(node.attribs).map(([name, value]) => [name.toLowerCase(), value]));
        const props = componentProps(attrs);
        const index = componentIndex++;
        // Old or incomplete builder records must not turn the whole SSR page into an error.
        const config = props.config;
        if (config && typeof config === "object" && !Array.isArray(config)) {
          return { type: "component", tag, props, mountId: mountId(tag, attrs, index) };
        }
      }
      const children = node.children.map(convert);
      if (children.some(child => child.type !== "static")) {
        return { type: "element", tag: node.name, attrs: node.attribs, children };
      }
    }
    // Keep ordinary editor HTML byte-for-byte, including SVG, entities and inline styles.
    return { type: "static", html: html.slice(node.startIndex ?? 0, (node.endIndex ?? -1) + 1) };
  };
  return document.children.map(convert);
}

export function renderSectionContent(nodes: SectionNode[], registry: Record<string, Component>): VNode[] {
  return nodes.map(node => {
    if (node.type === "static") return createStaticVNode(node.html, 1);
    if (node.type === "component") {
      // Preserve the wrapper used by the existing editor CSS and mounted sections.
      return h("div", { id: node.mountId, key: node.mountId }, [
        h(SectionBlock, { component: registry[node.tag]!, componentProps: node.props }),
      ]);
    }
    return h(node.tag, node.attrs, renderSectionContent(node.children, registry));
  });
}
